import { useState } from 'react';
import { api } from '../lib/api';
import { chooseSerialPort, loadDrawer, openDrawer, saveDrawer, serialSupported, type DrawerConfig } from '../lib/drawer';
import { ErrorNote, Modal } from './ui';

// How this register computer opens its cash drawer. The choice is kept on this computer only.
export default function DrawerSetup({ onClose, canNoSale }: { onClose: () => void; canNoSale: boolean }) {
  const [cfg, setCfgState] = useState<DrawerConfig>(loadDrawer());
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const set = (p: Partial<DrawerConfig>) => { const n = { ...cfg, ...p }; setCfgState(n); saveDrawer(n); setMsg(''); };
  async function connect() {
    setErr(null);
    try { const p = await chooseSerialPort(); set({ vid: p.vid, pid: p.pid, portLabel: p.label }); setMsg(`Connected: ${p.label}. Press Test to try it.`); } catch (e) { if ((e as Error).name !== 'NotFoundError') setErr(e); }
  }
  async function test() {
    setErr(null); setMsg('');
    const r = await openDrawer(cfg);
    setMsg(r.ok ? 'Sent. The drawer should have popped open. If it did not, check the cable and the choice above.' : r.message ?? 'It did not open.');
  }
  async function noSale() {
    setErr(null);
    try { await api('/api/drawer/no-sale', { body: {} }).catch(() => {}); } finally { const r = await openDrawer(cfg); setMsg(r.ok ? 'Opened (no sale). This is recorded.' : r.message ?? 'It did not open.'); }
  }
  return (
    <Modal title="Cash drawer on this computer" onClose={onClose} wide>
      <ErrorNote error={err} />
      {msg && <div className={`notice ${/did not|No port|cannot|off/i.test(msg) ? 'bad' : 'good'}`}>{msg}</div>}
      <label>How does it open?
        <select value={cfg.method} onChange={(e) => set({ method: e.target.value as DrawerConfig['method'] })}>
          <option value="off">It does not (switched off)</option>
          <option value="serial">Through the printer's serial / COM port</option>
          <option value="print">By printing a blank page (the printer driver opens the drawer)</option>
        </select></label>

      {cfg.method === 'serial' && (
        <div className="stack">
          {!serialSupported() && <div className="notice bad">This browser cannot use a serial port. Use Chrome or Edge on the register computer.</div>}
          <div className="row wrap" style={{ gap: 8, alignItems: 'end' }}>
            <button className="primary" disabled={!serialSupported()} onClick={connect}>{cfg.portLabel ? 'Choose a different port' : 'Choose the printer or drawer port'}</button>
            <label>Speed<select value={cfg.baud} onChange={(e) => set({ baud: Number(e.target.value) })}>{[9600, 19200, 38400, 57600, 115200].map((b) => <option key={b} value={b}>{b}</option>)}</select></label>
            {cfg.portLabel && <span className="chip good">{cfg.portLabel}</span>}
          </div>
          <p className="faint">Your receipt printer must appear as a COM port (many USB printers do after their "virtual COM" driver is installed). A drawer plugged into the printer's drawer socket opens from the printer. The browser asks you once and remembers.</p>
        </div>)}
      {cfg.method === 'print' && (
        <div className="stack">
          <p className="faint">In Windows, open the receipt printer's <b>Printing preferences</b> (or the printer's own utility) and set <b>Open cash drawer: before or after printing a document</b>. Then every print opens the drawer.</p>
          <p className="faint">To stop the print window from appearing each time, start Chrome with <code>--kiosk-printing</code> (right-click the Chrome shortcut, Properties, add it at the end of Target) and make the receipt printer the Windows default printer.</p>
        </div>)}

      {cfg.method !== 'off' && (
        <div className="stack">
          <label className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)', gap: 6 }}><input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={cfg.onCash} onChange={(e) => set({ onCash: e.target.checked })} /> Open the drawer when a sale takes cash</label>
          <div className="row wrap" style={{ gap: 8 }}>
            <button onClick={test}>Test: open the drawer now</button>
            {canNoSale && <button onClick={noSale}>No sale (open without a sale)</button>}
          </div>
          <p className="faint">"No sale" is recorded with who did it, and it shows up on the daily fraud check, so use it only for change or a cash count.</p>
        </div>)}
      <p className="faint">This setting is for this computer only. Set it up on each register.</p>
    </Modal>
  );
}
